import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Landmark } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminTreasuryClassifyButton — Round P148 (Jun 2026).
 *
 * One-shot trigger for the weekly treasury-vehicle / preferred
 * classify sweep (Sun 4:30 AM ET cron). Scans stock_cache rows whose
 * name/description hints preferred / perpetual / treasury, pulls
 * Massive ticker details, and stamps is_preferred /
 * is_treasury_vehicle (SATA / STRK / STRC blueprint) /
 * is_treasury_strategy (soft, e.g. MSTR). REIT/BDC rows are guarded.
 * Fire-and-forget: results land in the cron history dropdown.
 */
export default function AdminTreasuryClassifyButton() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.role !== 'admin') return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/run-treasury-classify-now`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Treasury classify sweep started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start treasury classify sweep');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-violet-300/60 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 text-violet-800 dark:text-violet-300 hover:bg-violet-100/70 dark:hover:bg-violet-900/40 transition-colors disabled:opacity-50"
      title="Weekly Sun 4:30 AM ET cron. Stamps is_preferred / is_treasury_vehicle (PFD + treasury keywords, e.g. SATA/STRK/STRC) and is_treasury_strategy (soft, e.g. MSTR) from Massive reference data. REIT/BDC guarded. Results in cron history."
      data-testid="admin-treasury-classify-btn"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />}
      Treasury Vehicle Classify
    </button>
  );
}
